import { parseMaybeJson } from 'core/utils/json.utils';
import { useCallback, useEffect, useState } from 'react';
import { createQuizRevisionConversation } from '../services/quizRevisionService';

/**
 * Hook to manage quiz revision conversation creation
 * @param {Object} job - Quiz job object with metadata
 * @param {string} userId - External user ID
 * @param {boolean} autoCreate - Auto-create on mount (default: false)
 * @param {Object} questionsLearningObjectives - Map of questionGuid to learning objectives array
 * @returns {Object} { isCreating, conversations, createConversations }
 */
export function useQuizRevisionConversations(job, userId, autoCreate = false, questionsLearningObjectives = {}) {
  const [isCreating, setIsCreating] = useState(autoCreate);
  const [conversations, setConversations] = useState({});

  const createConversations = useCallback(async () => {
    if (!job?.metadata || !userId) {
      return;
    }

    setIsCreating(true);

    const parseJobDataToArray = (jsonString) => {
      try {
        const parsed = JSON.parse(jsonString);

        if (Array.isArray(parsed)) {
          return parsed;
        }

        if (typeof parsed === 'object' && parsed !== null) {
          return Object.keys(parsed)
            .sort((a, b) => Number(a) - Number(b))
            .map((key) => parsed[key]);
        }

        throw new Error('Unexpected format');
      } catch (error) {
        console.error('Failed to parse:', error.message);
        return [];
      }
    };

    try {
      const metadata = JSON.parse(job.metadata);
      const jobData = parseJobDataToArray(job.data);

      if (!metadata.answers || metadata.answers.length === 0) {
        console.warn('No quiz questions found in metadata');
        return;
      }

      // Create payloads for each question
      const payloads = metadata.answers.map((answer, index) => {
        // TODO: FALLBACK BASED ON INDEX - THIS HAS RELIABILITY ISSUES TO REVIEW.
        // Reason for the fallback is that the metadata in the job has multiple formats (2 identified) and the questionGuid is not available in one of the 2 formats
        // The metadata and data are saved from the front and there are multiple locations where the student can solve activities and the hypothesis is that the way the metadata and data are saved are different.
        const questionGuid = answer?.questionGuid || jobData[index]?.guid;
        if (!questionGuid) {
          console.warn('[useQuizRevisionConversations] Missing questionGuid in answer payload');
        }
        // Get learning objectives for this question from the map
        const learningObjectives = (questionGuid && questionsLearningObjectives[questionGuid]) || [];

        // Map learning objectives to the expected format
        // Extract cognitive phase and microhability from the parent learning objective. Nanohability is the main learning objective.
        // We only send the first learning objective for now as the main one
        const parentLearningObjective = learningObjectives[0]?.parent?.learning_objective;
        const parentLearningObjectiveArray = parentLearningObjective?.split(' - ') || [];
        const cognitivePhase = parentLearningObjectiveArray[0] || '';
        const microhability = parentLearningObjectiveArray[1] || '';
        const nanohability = learningObjectives[0]?.learning_objective || '';

        const mappedLearningObjectives = {
          cognitivePhase: cognitivePhase,
          microhability: microhability,
          nanohability: nanohability,
        };

        return {
          externalUserId: userId,
          quizJobGuid: job.guid,
          questionReferenceId: questionGuid,
          quizQuestionData: { ...answer, question: parseMaybeJson(answer.question), answer: parseMaybeJson(answer.answer) },
          learningObjectives: mappedLearningObjectives,
        };
      });

      // Create all conversations in parallel
      const promises = payloads.map((payload) =>
        createQuizRevisionConversation(payload)
          .then((response) => {
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'ready',
              conversationId: response.conversationId,
              error: null,
            };
          })
          .catch((error) => {
            console.error('[useQuizRevisionConversations] Failed for question:', payload.questionReferenceId, error);
            return {
              questionReferenceId: payload.questionReferenceId,
              status: 'failed',
              conversationId: null,
              error: error.message,
            };
          })
      );

      const results = await Promise.all(promises);

      // Build conversations state map
      const conversationsMap = {};
      results.forEach((result) => {
        conversationsMap[result.questionReferenceId] = result;
      });

      setConversations(conversationsMap);
    } catch (error) {
      console.error('Failed to create quiz revision conversations:', error);
    } finally {
      setIsCreating(false);
    }
  }, [job, userId, questionsLearningObjectives]);

  // Auto-create on mount if enabled
  useEffect(() => {
    if (autoCreate && job && userId) {
      createConversations();
    }
  }, [autoCreate, job, userId, createConversations]);

  return {
    isCreating,
    conversations,
    createConversations, // Expose for manual retry
  };
}
